import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { useNavigate } from 'react-router-dom';

export default function SpeedAgilityPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-speed-agility';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Speed & Agility Training | Elite Performance Lab Corsicana TX",
      "description": "12-week speed and agility development program for baseball and multi-sport athletes. Acceleration, deceleration, lateral quickness, and reactive agility training in Corsicana, TX.",
      "url": `${import.meta.env.VITE_SITE_URL}/speed-agility`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Speed & Agility", "item": `${import.meta.env.VITE_SITE_URL}/speed-agility` }
        ]
      },
      "mainEntity": {
        "@type": "Course",
        "name": "12-Week Speed & Agility Development Program",
        "description": "A structured, progressive 3-phase training system designed to build elite speed and agility. 2 sessions per week, 24 total sessions.",
        "provider": {
          "@type": "Organization",
          "name": "Elite Performance Lab",
          "address": { "@type": "PostalAddress", "addressLocality": "Corsicana", "addressRegion": "TX", "addressCountry": "US" }
        },
        "offers": {
          "@type": "Offer",
          "price": "850",
          "priceCurrency": "USD",
          "description": "Total for 3 months (or $283.34/month billed monthly)"
        }
      }
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-speed-agility');
      if (el) el.remove();
    };
  }, []);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/82bc3aea61a1460b36a4651b0a8fb8e1.jpg"
          alt="Speed and Agility Training"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Dominate Every Step
          </h1>
          <p className="text-xl lg:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Elite speed and agility training designed for baseball athletes who refuse to be outrun, outmaneuvered, or outplayed
          </p>
          <button
            onClick={scrollToContact}
            className="px-10 py-4 bg-teal-500 text-white text-lg font-bold rounded-full hover:bg-teal-600 transition-all shadow-2xl whitespace-nowrap cursor-pointer"
          >
            Schedule Your Assessment
          </button>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">Program Pricing</h2>
          <p className="text-lg text-gray-300 mb-12 max-w-2xl mx-auto">
            Invest in your athletic development with a structured 12-week speed &amp; agility program.
          </p>

          <div className="bg-gray-800 border-2 border-teal-500 rounded-2xl p-10 max-w-md mx-auto shadow-2xl relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-teal-500 text-white text-sm font-bold px-5 py-1.5 rounded-full whitespace-nowrap">
                12-Week Program
              </span>
            </div>

            <div className="mb-6">
              <div className="text-6xl font-bold text-white mb-1">$850</div>
              <p className="text-gray-400 text-base">Total for 3 months</p>
            </div>

            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl px-6 py-4 mb-8">
              <p className="text-teal-400 font-semibold text-lg">
                or <span className="text-white text-2xl font-bold">$283.34</span> / month
              </p>
              <p className="text-gray-400 text-sm mt-1">Billed monthly over 3 months</p>
            </div>

            <ul className="space-y-3 text-left mb-8">
              <li className="flex items-center gap-3 text-gray-300">
                <i className="ri-check-line text-teal-500 text-xl"></i>
                <span>24 total training sessions</span>
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <i className="ri-check-line text-teal-500 text-xl"></i>
                <span>2 sessions per week</span>
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <i className="ri-check-line text-teal-500 text-xl"></i>
                <span>3-phase progressive training system</span>
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <i className="ri-check-line text-teal-500 text-xl"></i>
                <span>Performance testing (Week 1, 5, 9 &amp; 12)</span>
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <i className="ri-check-line text-teal-500 text-xl"></i>
                <span>Sport-specific programming</span>
              </li>
            </ul>

            <button
              onClick={scrollToContact}
              className="w-full py-4 bg-teal-500 text-white text-lg font-bold rounded-full hover:bg-teal-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* 12-Week Program Overview */}
      <section className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              12-Week Speed &amp; Agility Development Cycle
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              A structured, progressive training system designed to build elite speed and agility through three distinct phases. 2 sessions per week, 24 total sessions.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-900 p-8 rounded-lg border-2 border-teal-500/30 hover:border-teal-500 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl font-bold text-teal-500">1</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Phase 1</h3>
                  <p className="text-gray-400 text-sm">Weeks 1–4</p>
                </div>
              </div>
              <h4 className="text-xl font-bold text-white mb-4">Movement Foundation &amp; Ground Force</h4>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Build movement quality, proprioception, and impulse into the ground. Fix acceleration mechanics and ground contact strategy.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Proprioception &amp; Stability</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Acceleration Mechanics</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Plyometric Foundation</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Rotational Power</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg border-2 border-teal-500/30 hover:border-teal-500 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl font-bold text-teal-500">2</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Phase 2</h3>
                  <p className="text-gray-400 text-sm">Weeks 5–8</p>
                </div>
              </div>
              <h4 className="text-xl font-bold text-white mb-4">Elastic Power &amp; Acceleration</h4>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Develop rate of force development and elastic rebound. Train the stretch-shortening cycle with increased intensity.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Elastic Plyometrics</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Acceleration Development</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Rotational Force Development</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Change of Direction</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg border-2 border-teal-500/30 hover:border-teal-500 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl font-bold text-teal-500">3</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Phase 3</h3>
                  <p className="text-gray-400 text-sm">Weeks 9–12</p>
                </div>
              </div>
              <h4 className="text-xl font-bold text-white mb-4">Max Speed &amp; Reactive Agility</h4>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Maximize top speed, improve reaction time, and develop explosive impulse for game-speed performance.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Advanced Plyometrics</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Max Velocity Mechanics</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Reactive Agility</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <i className="ri-check-line text-teal-500 text-xl mt-0.5"></i>
                  <span>Game Speed Drills</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Expected Results (12 Weeks)</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <div className="text-3xl font-bold text-teal-500 mb-2">0.08–0.15s</div>
                <p className="text-gray-300 text-sm">10 Yard Speed Improvement</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-500 mb-2">0.15–0.30s</div>
                <p className="text-gray-300 text-sm">30 Yard Speed Improvement</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-500 mb-2">+3–6"</div>
                <p className="text-gray-300 text-sm">Vertical Jump Increase</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-500 mb-2">+10–20%</div>
                <p className="text-gray-300 text-sm">Rotational Power Gain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Train Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              What We Train
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Every movement matters in competition. We break down speed and agility into precise, measurable components — then build a program tailored to your sport and position.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 hover:border-teal-500 transition-all">
              <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-speed-line text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Acceleration</h3>
              <p className="text-gray-300 leading-relaxed">
                Master the explosive first steps that separate good athletes from elite ones. We train the neuromuscular patterns that create instant separation — no matter what sport you play.
              </p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 hover:border-teal-500 transition-all">
              <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-stop-circle-line text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Deceleration</h3>
              <p className="text-gray-300 leading-relaxed">
                Control your momentum and stop on a dime without injury. Proper deceleration mechanics are essential for every sport — protecting your body while keeping you in the play.
              </p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 hover:border-teal-500 transition-all">
              <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-arrow-left-right-line text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Lateral Quickness</h3>
              <p className="text-gray-300 leading-relaxed">
                Develop the side-to-side explosiveness that defines elite athletes across every sport. React faster, cover more ground, and make the plays others can't.
              </p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 hover:border-teal-500 transition-all">
              <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">First-Step Explosiveness</h3>
              <p className="text-gray-300 leading-relaxed">
                Train the reactive power that creates instant reads and explosive starts. Milliseconds matter at every level — we build the quickness that gives you the edge.
              </p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 hover:border-teal-500 transition-all">
              <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-route-line text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Change of Direction</h3>
              <p className="text-gray-300 leading-relaxed">
                Perfect the cutting mechanics that let you change direction without losing speed. Built for the specific movement demands of your sport and position.
              </p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 hover:border-teal-500 transition-all">
              <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-footprint-line text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Multi-Directional Movement</h3>
              <p className="text-gray-300 leading-relaxed">
                Integrate forward, backward, and lateral movements into seamless athletic patterns. Train the way you compete — unpredictable, reactive, and explosive in every direction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Speed & Agility Matters Section */}
      <section className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Built for Every Sport. Tailored to You.
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Speed and agility are the foundation of elite athletic performance — no matter what sport you play. Every program is customized to the specific demands of your sport and position.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="bg-gray-900 p-8 rounded-lg text-center border border-gray-700">
              <div className="text-5xl font-bold text-teal-500 mb-3">0.1s</div>
              <p className="text-gray-300 leading-relaxed">
                Difference between winning and losing a contested play at any level
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg text-center border border-gray-700">
              <div className="text-5xl font-bold text-teal-500 mb-3">20%</div>
              <p className="text-gray-300 leading-relaxed">
                Average improvement in first-step quickness after 8 weeks of targeted training
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg text-center border border-gray-700">
              <div className="text-5xl font-bold text-teal-500 mb-3">All</div>
              <p className="text-gray-300 leading-relaxed">
                Sports benefit — baseball, football, soccer, basketball, lacrosse, and more
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg text-center border border-gray-700">
              <div className="text-5xl font-bold text-teal-500 mb-3">100%</div>
              <p className="text-gray-300 leading-relaxed">
                Sport-specific programming — no generic templates, every plan is built for your game
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-lg border border-gray-700">
              <h3 className="text-2xl font-bold text-white mb-4">Field &amp; Court Sports</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Soccer, lacrosse, football, field hockey — every sport demands explosive acceleration, sharp cuts, and the ability to cover ground faster than your opponent.
              </p>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Explosive acceleration off the line</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Sharp directional changes</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Sustained speed over distance</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg border border-gray-700">
              <h3 className="text-2xl font-bold text-white mb-4">Court &amp; Track Sports</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Basketball, volleyball, tennis, track — elite performance requires reactive quickness, vertical explosiveness, and the ability to reset and explode again and again.
              </p>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Reactive first-step quickness</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Lateral shuffle &amp; drop-step mechanics</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Elastic power &amp; plyometric ability</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg border border-gray-700">
              <h3 className="text-2xl font-bold text-white mb-4">Recruiting &amp; Showcases</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                College coaches and pro scouts across every sport prioritize athleticism. Measurable speed and agility metrics open doors at every level of competition.
              </p>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Stand out at combines &amp; showcases</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Objective, trackable athletic metrics</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                  <span>Competitive edge over your peers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-500">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Get Faster?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Schedule your speed and agility assessment today. Let's measure where you are and build a plan to get you where you want to be.
          </p>
          <button
            onClick={scrollToContact}
            className="px-10 py-4 bg-white text-teal-600 text-lg font-bold rounded-full hover:bg-gray-100 transition-all shadow-2xl whitespace-nowrap cursor-pointer"
          >
            Schedule Your Assessment
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}